import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import {
  cfRealtimeQueryFilters,
  refreshFeedCommentsRealtime,
  resolveFeedDetailRealtimeTarget,
  type WorkspaceRealtimeScope,
} from "@/modules/cf-feed/presentation/api/feed-detail-realtime";
import {
  type CfRealtimeEvent,
  isCfRealtimeEvent,
} from "@/modules/realtime/domain/contracts/realtime-event";
import {
  CF_AUTH_EXPIRED_EVENT,
  getCfSessionStatus,
  markCfAuthExpired,
  shouldBlockCfApiRequests,
  showCfAuthExpiredDialog,
} from "@/shared/lib/cf-auth-session";

const RECONNECT_MAX_MS = 30_000;
const INVALIDATE_DEBOUNCE_MS = 500;
const INVALIDATE_MAX_WAIT_MS = 1_000;
const RealtimeConnectedContext = createContext(false);
export const useRealtimeConnected = () => useContext(RealtimeConnectedContext);

/** รับ event ขนาดเล็กแล้ว refetch query ที่เกี่ยวข้อง — feed detail ใช้ targeted keys */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useAuthState();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    // ยังไม่ login → ไม่เปิด SSE (กัน 401 spam + reconnect วน)
    if (isLoading || !isAuthenticated) {
      setConnected(false);
      return;
    }

    let closed = false;
    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
    let invalidateBatchStartedAt: number | null = null;
    let pendingEvents: CfRealtimeEvent[] = [];
    let pendingBroad = false;
    let reconnectDelay = 1_000;
    let connectedOnce = false;
    const seenIds = new Set<string>();
    const workspaceScopes = () =>
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ["workspace"] })
        .flatMap((query) => {
          const data = query.state.data as WorkspaceRealtimeScope | undefined;
          return data?.id ? [data] : [];
        });

    const flushInvalidations = () => {
      invalidateTimer = null;
      invalidateBatchStartedAt = null;
      const batch = pendingEvents;
      const broad = pendingBroad;
      pendingEvents = [];
      pendingBroad = false;

      if (broad) {
        void queryClient.invalidateQueries(
          { refetchType: "active" },
          { cancelRefetch: true },
        );
        return;
      }

      const handledFilters = new Set<string>();
      const handledComments = new Set<string>();
      const scopes = workspaceScopes();
      for (const event of batch) {
        for (const filter of cfRealtimeQueryFilters(
          event,
          window.location.pathname,
          scopes,
        )) {
          const key = JSON.stringify([filter.queryKey, filter.exact ?? false]);
          if (handledFilters.has(key)) continue;
          handledFilters.add(key);
          void queryClient.invalidateQueries(
            { ...filter, refetchType: "active" },
            { cancelRefetch: true },
          );
        }

        if (event.type === "comment.changed") {
          const feedId = resolveFeedDetailRealtimeTarget({
            event,
            pathname: window.location.pathname,
          });
          if (!feedId || handledComments.has(feedId)) continue;
          handledComments.add(feedId);
          void refreshFeedCommentsRealtime(queryClient, feedId).catch(
            (error) => {
              console.error("[cf-realtime] refresh comments failed", error);
            },
          );
        }
      }
    };

    const scheduleFlush = (input?: {
      event?: CfRealtimeEvent;
      broad?: boolean;
    }) => {
      if (input?.broad) pendingBroad = true;
      if (input?.event) pendingEvents.push(input.event);
      const now = performance.now();
      invalidateBatchStartedAt ??= now;
      const elapsed = now - invalidateBatchStartedAt;
      const wait = Math.min(
        INVALIDATE_DEBOUNCE_MS,
        Math.max(0, INVALIDATE_MAX_WAIT_MS - elapsed),
      );
      if (invalidateTimer) clearTimeout(invalidateTimer);
      invalidateTimer = setTimeout(flushInvalidations, wait);
    };

    const connect = () => {
      if (closed || shouldBlockCfApiRequests()) return;
      source = new EventSource("/api/realtime/events", {
        withCredentials: true,
      });
      source.addEventListener("open", () => {
        reconnectDelay = 1_000;
        if (connectedOnce) {
          // reconnect → sync active ทั้งชุดครั้งเดียว
          pendingEvents = [];
          scheduleFlush({ broad: true });
        }
        connectedOnce = true;
      });
      source.addEventListener("resync", () => {
        setConnected(true);
        scheduleFlush({ broad: true });
      });
      source.addEventListener("cf-change", (raw) => {
        const message = raw as MessageEvent<string>;
        try {
          const parsed: unknown = JSON.parse(message.data);
          if (!isCfRealtimeEvent(parsed)) {
            console.warn("[cf-realtime] ignored invalid event", parsed);
            return;
          }
          setConnected(true);
          if (seenIds.has(parsed.id)) return;
          seenIds.add(parsed.id);
          if (seenIds.size > 1000) {
            const oldest = seenIds.values().next().value;
            if (oldest) seenIds.delete(oldest);
          }
          if (
            cfRealtimeQueryFilters(
              parsed,
              window.location.pathname,
              workspaceScopes(),
            ).length === 0
          ) {
            return;
          }
          scheduleFlush({ event: parsed });
        } catch (error) {
          console.warn("[cf-realtime] ignored malformed event", error);
        }
      });
      source.addEventListener("error", () => {
        setConnected(false);
        source?.close();
        source = null;
        if (closed || reconnectTimer) return;
        void (async () => {
          const sessionStatus = await getCfSessionStatus();
          if (closed) return;
          if (sessionStatus === "expired") {
            markCfAuthExpired();
            void showCfAuthExpiredDialog({ checkSession: false });
            return;
          }
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            connect();
          }, reconnectDelay);
          reconnectDelay = Math.min(RECONNECT_MAX_MS, reconnectDelay * 2);
        })();
      });
    };

    const stopForExpiredSession = () => {
      setConnected(false);
      closed = true;
      source?.close();
      source = null;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
    };

    window.addEventListener(CF_AUTH_EXPIRED_EVENT, stopForExpiredSession);
    connect();
    return () => {
      closed = true;
      source?.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (invalidateTimer) clearTimeout(invalidateTimer);
      window.removeEventListener(CF_AUTH_EXPIRED_EVENT, stopForExpiredSession);
    };
  }, [queryClient, isAuthenticated, isLoading]);

  return (
    <RealtimeConnectedContext.Provider value={connected}>
      {children}
    </RealtimeConnectedContext.Provider>
  );
}
